/**
 * 身分表（`#/identities` 的主體）：**一列一顆身分，四個狀態同一列**。
 *
 * 為什麼是這個形狀（plan-console-4zones-split.md T02／T11）：
 * - 資料層是 `identityRows`（`packages/console-ui/src/types.ts` 的 `IdentityRow`）：憑證列與
 *   在線 session 已對齊，四態判定在資料層，呈現層不自己算。
 * - 表格本身交給 `EntityTable`（`packages/console-ui/src/components/EntityTable.tsx`）：
 *   欄位描述只寫一份，桌機表格與手機卡片都從 `IDENTITY_COLUMNS` 長出來，兩個寬度看到的欄位
 *   集合必然相同；空態、載入態、排序、篩選條的殼也都在那裡。
 * - 這一頁只留**這一頁才有的東西**：四態標籤怎麼畫、篩選規則（`filterIdentityRows`）、
 *   篩選鈕上的數字（同一份規則算出來，另一處不重算）。
 *
 * 硬規則：
 * - `null`（後端沒回報）畫成「未回報」、時間沒有紀錄畫成「無紀錄」，**不畫成 `false`／`0`／`—`**。
 * - 沒有 session 的身分沒有東西可勾（批次動作的對象是 session），checkbox 停用並說明原因。
 * - 預設排序＝最後活動由新到舊（`EntityTable` 的 `defaultSort`），不必先按表頭。
 */
import { useMemo, useState } from 'react';
import { EntityFilterBar, EntityTable, type EntityColumn } from '../../../../packages/console-ui/src/components/EntityTable';
import type { IdentityRow, IdentityState } from '../../../../packages/console-ui/src/identity-rows';

/**
 * 篩選條的三種看法。`issues`＝缺憑證或憑證壞（四態裡的兩個壞狀態）。
 *
 * 標籤用「待處理」不是「有問題」：頂部那格講的是**憑證**，這裡講的是**身分層面**，
 * 兩個都寫「健康／有問題」在手機上會被讀成同一件事、看起來像自打臉。
 */
export const IDENTITY_FILTERS = [
  { id: 'all', label: '全部' },
  { id: 'issues', label: '待處理' },
  { id: 'online', label: '在線' },
] as const;

export type IdentityFilterId = (typeof IDENTITY_FILTERS)[number]['id'];

/**
 * 四態的長相。顏色全部走既有 CSS 原語（`cui-health-dot--*` 在 `console-ui/styles.css`），
 * 不在這裡新造顏色，也不新增 CSS：`live` 綠、`cred-broken` 琥珀、`no-cred` 紅、`idle` 灰。
 */
const STATE_VIEW: Record<IdentityState, { label: string; dot: string; text: string; hint: string }> = {
  live: {
    label: '在話中',
    dot: 'cui-health-dot cui-health-dot--online',
    text: 'text-success',
    hint: '有 session 正持有憑證並在線',
  },
  idle: {
    label: '待用',
    dot: 'cui-health-dot',
    text: 'text-text-tertiary',
    hint: '有憑證、目前沒有在回話',
  },
  'cred-broken': {
    label: '憑證壞',
    dot: 'cui-health-dot cui-health-dot--limited',
    text: 'text-warning',
    hint: '有憑證，但上次登入／註冊失敗',
  },
  'no-cred': {
    label: '缺憑證',
    dot: 'cui-health-dot cui-health-dot--down',
    text: 'text-danger',
    hint: '池裡沒有可用憑證，要重新登入／註冊',
  },
};

/** 憑證欄：有／缺／壞／未回報。`壞` 只在四態判定為 `cred-broken` 時出現。 */
function credText(row: IdentityRow): { text: string; tone: string } {
  if (row.state === 'cred-broken') return { text: '壞', tone: 'text-warning' };
  if (row.hasCred === true) return { text: '有', tone: 'text-success' };
  if (row.hasCred === false) return { text: '缺', tone: 'text-danger' };
  return { text: '未回報', tone: 'text-text-tertiary' };
}

/** 持有欄：佔用中／無／未回報。 */
function heldText(row: IdentityRow): string {
  if (row.held === true) return '佔用中';
  if (row.held === false) return '無';
  return '未回報';
}

/** 在線欄：在線 · 房號／離線／沒開／未回報。 */
function liveText(row: IdentityRow): string {
  if (row.sessionCount === 0) return '沒開';
  if (row.online === true) return row.room ? `在線 · ${row.room}` : '在線';
  if (row.online === false) return '離線';
  return '未回報';
}

/** 相對時間；`null` 是「沒有紀錄」，不是「剛剛」。 */
function formatWhen(ts: number | null, now: number): string {
  if (ts === null) return '無紀錄';
  const diff = Math.max(0, now - ts);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) return '剛剛';
  if (diff < hour) return `${Math.floor(diff / minute)} 分前`;
  if (diff < day) return `${Math.floor(diff / hour)} 時前`;
  const date = new Date(ts);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getMonth() + 1}/${date.getDate()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** 顯示名稱優先；沒有就退回帳號本身（截短交給 CSS `truncate`，不改資料）。 */
function labelOf(row: IdentityRow): string {
  return row.displayName ?? row.username;
}

/** 「有問題」＝缺憑證或憑證壞。用四態判定，不另外看 `ok`／`hasToken`。 */
function isIssue(row: IdentityRow): boolean {
  return row.state === 'no-cred' || row.state === 'cred-broken';
}

/**
 * 篩選＋搜尋的**唯一一份**規則：表格、手機卡片、篩選條上的數字都呼叫它。
 * 搜尋比對顯示名／帳號／站別／房號（全部轉小寫，不做模糊拼音）。
 */
export function filterIdentityRows(
  rows: readonly IdentityRow[],
  filter: IdentityFilterId,
  query: string,
): IdentityRow[] {
  const needle = query.trim().toLowerCase();
  return rows.filter((row) => {
    if (filter === 'issues' && !isIssue(row)) return false;
    if (filter === 'online' && row.online !== true) return false;
    if (needle === '') return true;
    const haystack = [labelOf(row), row.username, row.service, row.room ?? ''];
    return haystack.some((value) => value.toLowerCase().includes(needle));
  });
}

/** 篩選鈕上的數字（與過濾同一份規則，含搜尋）；沒有搜尋時就是該篩選的總數。 */
function countFor(rows: readonly IdentityRow[], filter: IdentityFilterId, query: string): number {
  return filterIdentityRows(rows, filter, query).length;
}

export interface IdentityTableProps {
  /** 篩選條上的對照說明（例：`憑證 16/16 · 待處理 11`）；讓兩個數字對得起來。 */
  rows: readonly IdentityRow[];
  /** 已勾選的 session id（跟 `c.sessionPicked` 同一份）。 */
  picked: readonly string[];
  /** 勾／取消一顆（`c.toggleSessionPick`）。 */
  onTogglePick: (sessionId: string) => void;
  /** 每列唯一那顆動作鈕；`null` 表示這列沒有可做的事（按鈕停用）。 */
  onAction: (row: IdentityRow) => void;
  /** 動作鈕文字（依列狀態給不同字：換身分／開啟／重新登入）。 */
  actionLabel: (row: IdentityRow) => string;
  /** 有動作正在跑時鎖住按鈕。 */
  busy?: boolean;
  /** 篩選條右側的一句對照說明（`null` 就不顯示）。 */
  hint?: string | null;
}

export function IdentityTable({
  rows,
  picked,
  onTogglePick,
  onAction,
  actionLabel,
  busy = false,
  hint = null,
}: IdentityTableProps) {
  /** 一次算好 `now`，避免同一列在 render 中途跳分鐘。 */
  const now = useMemo(() => Date.now(), [rows]);
  const pickedSet = useMemo(() => new Set(picked), [picked]);
  const [filter, setFilter] = useState<IdentityFilterId>('all');
  const [query, setQuery] = useState('');

  const visible = useMemo(() => filterIdentityRows(rows, filter, query), [rows, filter, query]);
  const counts = useMemo(
    () =>
      IDENTITY_FILTERS.reduce<Record<IdentityFilterId, number>>(
        (acc, item) => {
          acc[item.id] = countFor(rows, item.id, query);
          return acc;
        },
        { all: 0, issues: 0, online: 0 },
      ),
    [rows, query],
  );

  /** 欄位描述：唯一一份。桌機表頭與手機卡片欄位名都從 `label` 來。 */
  const columns = useMemo<EntityColumn<IdentityRow>[]>(
    () => [
      {
        id: 'identity',
        label: '身分',
        width: '26%',
        render: (row) => {
          const view = STATE_VIEW[row.state];
          return (
            <>
              {/* 兩行：第一行＝名稱（長名截短）＋站別；第二行＝狀態＋帳號。
                  名字、站別、狀態三者各有 8px 間距，手機上不會黏成一團。 */}
              <span className="cui-identity-line1 flex min-w-0 items-center gap-2">
                <span className={`${view.dot} shrink-0`} aria-hidden="true" data-testid="identity-state-dot" />
                <span className="min-w-0 flex-1 truncate font-medium" title={labelOf(row)} data-testid="identity-row-name">
                  {labelOf(row)}
                </span>
                <span className={`cui-badge cui-badge--${row.service} shrink-0`}>{row.service}</span>
              </span>
              <span className="cui-identity-line2 mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] text-text-tertiary">
                <span className={`${view.text} shrink-0 font-medium`} data-testid="identity-state-label" title={view.hint}>
                  {view.label}
                </span>
                <span className="min-w-0 truncate" title={row.username}>
                  {row.username}
                </span>
              </span>
            </>
          );
        },
      },
      {
        id: 'cred',
        label: '憑證',
        width: '10%',
        render: (row) => {
          const cred = credText(row);
          return (
            <span className={cred.tone} data-testid="identity-row-cred">
              {cred.text}
            </span>
          );
        },
      },
      {
        id: 'held',
        label: '持有',
        width: '12%',
        render: (row) => (
          <span
            className={row.held === true ? 'text-text-secondary' : 'text-text-tertiary'}
            data-testid="identity-row-held"
          >
            {heldText(row)}
          </span>
        ),
      },
      {
        id: 'live',
        label: '在線',
        width: '20%',
        render: (row) => (
          <span className="flex min-w-0 flex-wrap items-center gap-1" data-testid="identity-row-live">
            <span className={row.online === true ? 'truncate text-success' : 'truncate'}>{liveText(row)}</span>
            {row.sessionCount > 1 ? <span className="cui-badge">{row.sessionCount} 條</span> : null}
          </span>
        ),
      },
      {
        id: 'activity',
        label: '最後活動',
        width: '16%',
        // 預設排序吃這個值：最近活動優先，沒有紀錄的墊底（不是當成 0 排到最前面）。
        sortValue: (row) => row.lastActivityAt ?? row.lastUsedAt ?? null,
        render: (row) => (
          <span className="truncate text-text-tertiary" data-testid="identity-row-activity">
            {formatWhen(row.lastActivityAt ?? row.lastUsedAt, now)}
          </span>
        ),
      },
    ],
    [now],
  );

  return (
    <div
      className="cui-identity-table"
      data-testid="identity-table"
      data-rows={rows.length}
      data-visible-rows={visible.length}
    >
      <EntityTable
        rows={visible}
        columns={columns}
        rowKey={(row) => row.id}
        caption="身分表：憑證、持有、在線與最後活動"
        rowTestId="identity-row"
        rowAttrs={(row) => ({ 'data-identity': row.id, 'data-state': row.state })}
        defaultSort={{ columnId: 'activity', dir: 'desc' }}
        filterBar={
          <EntityFilterBar
            options={IDENTITY_FILTERS.map((item) => ({ ...item, count: counts[item.id] }))}
            value={filter}
            onChange={(id) => setFilter(id as IdentityFilterId)}
            query={query}
            onQueryChange={setQuery}
            queryPlaceholder="搜尋名稱／帳號／站別／房號"
            queryLabel="搜尋身分"
            shown={visible.length}
            total={rows.length}
            testId="identity-filters"
            testIdPrefix="identity-filter"
          >
            {hint ? (
              <span className="cui-identity-hint" data-testid="identity-filters-hint">
                {hint}
              </span>
            ) : null}
          </EntityFilterBar>
        }
        empty={
          rows.length === 0
            ? { title: '目前沒有身分', hint: '池裡還沒有可用的帳號；先到「系統」看憑證狀態。' }
            : {
                title: '沒有符合條件的身分',
                hint: `換一個關鍵字，或回到「全部」看 ${rows.length} 顆身分。`,
                action: {
                  label: '清除篩選',
                  onClick: () => {
                    setFilter('all');
                    setQuery('');
                  },
                },
              }
        }
        selection={{
          isPicked: (row) => row.sessionId !== null && pickedSet.has(row.sessionId),
          // 沒有 session 就沒有批次對象（批次動作用的 id 是 session id）。
          canPick: (row) => row.sessionId !== null,
          reason: () => '這顆沒有 session，沒有可批次處理的對象',
          label: (row) => `選取 ${labelOf(row)}`,
          onToggle: (row) => {
            if (row.sessionId !== null) onTogglePick(row.sessionId);
          },
          busy,
        }}
        rowAction={{
          label: actionLabel,
          onClick: onAction,
          disabled: busy,
          testId: 'identity-row-action',
        }}
      />
    </div>
  );
}
